import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface UploadConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fileCount?: number;
}

const UploadConfirmationModal: React.FC<UploadConfirmationModalProps> = ({ isOpen, onClose, onConfirm, fileCount = 1 }) => {
  if (!isOpen) return null;

  const fileText = fileCount === 1 ? 'file' : 'files';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[var(--color-modal-backdrop)] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[var(--color-modal-bg)] border border-[var(--color-border)] rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 hover:bg-engramiq-green/10 rounded text-[var(--color-text-secondary)] hover:text-engramiq-green transition-all"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Warning Icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[var(--color-overlay-medium)] rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-[var(--color-text-primary)]" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">Ready to process this upload?</h2>
        </div>

        {/* Message */}
        <p className="text-sm text-[var(--color-text-primary)] leading-relaxed mb-6">
          We're about to start processing {fileCount} {fileText}.<br />
          Once you accept, the upload can't be undone, and processing charges will be billed to your Engramiq account.
        </p>

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/40 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq"
          >
            Accept & process
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadConfirmationModal;
